import http, { handleApiResponse } from "@/services/http";
import ApiResponse from "@/models/ApiResponse";
import {
  CashPreAccError,
  CashPreAccLookups,
  CashPreAccReAccountResult,
  CashPreAccSetStatusResult,
} from "../models/CashPreAccounting";
import {
  CashPreAccCar,
  CashPreAccEditorData,
  CashPreAccPo,
  CashPreAccProject,
  CashPreAccSaveRequest,
  CashPreAccSaveResult,
  CashPreAccTool,
  CashPreAccUploadFlagsResult,
  CashPreAccUploadItem,
  CashPreAccValidate,
} from "../models/CashPreAccEditing";

export default class CashPreAccountingApi {
  // Böngésző lookupok (kontír-státuszok, periódusok)
  static getLookups(): Promise<CashPreAccLookups> {
    return http
      .get<ApiResponse<CashPreAccLookups>>(`cash-pre-accounting/lookups`)
      .then(handleApiResponse);
  }

  // Egy kivonat kontírozási hibalistája (K2D pb_err → mmacccashdocerr)
  static getErrors(id: number): Promise<CashPreAccError[]> {
    return http
      .get<ApiResponse<CashPreAccError[]>>(`cash-pre-accounting/${id}/errors`)
      .then(handleApiResponse);
  }

  // A kijelölt kivonatok (újra)kontírozása a BÖNGÉSZŐBŐL (K2D pb_cont → f_cash_cont_os);
  // kivonatonként külön tranzakció fut, a hibák a failures listában jönnek vissza
  static reAccount(statements: number[]): Promise<CashPreAccReAccountResult> {
    return http
      .post<ApiResponse<CashPreAccReAccountResult>>(`cash-pre-accounting/re-account`, {
        statements,
      })
      .then(handleApiResponse);
  }

  // EGY kivonat újrakontírozása a SZERKESZTŐBŐL (K2D pb_recontir). Külön végpont,
  // mert a K2D-ben itt bővebb a kontír státusz kapu, mint a böngésző csoportos
  // gombjánál: a 3-as "Feladható" státusz is újrakontírozható.
  static reAccountSingle(statementId: number): Promise<CashPreAccReAccountResult> {
    return http
      .post<ApiResponse<CashPreAccReAccountResult>>(
        `cash-pre-accounting/${statementId}/re-account`, {})
      .then(handleApiResponse);
  }

  //---------- Státusz állítás (K2D m_bank_cash.m_status menü) ----------//

  // Csoportos kontír státusz állítás (K2D ue_acc_status pénztár ága: "Nem adható fel" = 2,
  // "Feladható" = 3, "Nem feladandó" = 9). A művelet egy tranzakció, a nem megfelelő
  // státuszú kivonatok a failures listában jönnek vissza.
  static setStatuses(status: number, statements: number[]): Promise<CashPreAccSetStatusResult> {
    return http
      .post<ApiResponse<CashPreAccSetStatusResult>>(
        `cash-pre-accounting/set-status/${status}`,
        { statements }
      )
      .then(handleApiResponse);
  }

  // Csoportos "Feladva": a kijelölt (Feladható) kivonatok feladása a belső főkönyvbe
  // (K2D Case 4 → spmacc_bookhanding, prefix 'CASH'). allowAutoPromote = az "egyből
  // feladva" jogosultság. Bármely kivonat hibája az egész műveletet visszagördíti.
  static book(
    statements: number[],
    allowAutoPromote: boolean
  ): Promise<CashPreAccSetStatusResult> {
    return http
      .post<ApiResponse<CashPreAccSetStatusResult>>(`cash-pre-accounting/book`, {
        statements,
        allowAutoPromote,
      })
      .then(handleApiResponse);
  }

  // Csoportos "Feladásra előkészítve / visszavétel a főkönyvből" (K2D Case 5, 31):
  // a Feladható kivonat előkészítettbe kerül, a már feladott (4/6) vissza a főkönyvből.
  static takeBack(statements: number[]): Promise<CashPreAccSetStatusResult> {
    return http
      .post<ApiResponse<CashPreAccSetStatusResult>>(`cash-pre-accounting/take-back`, {
        statements,
      })
      .then(handleApiResponse);
  }

  //---------- Kontír-szerkesztő ("Adott pénztárkivonat kontírozási tételei") ----------//

  // A szerkesztő teljes betöltési csomagja (fej + tételek + bontás + lookupok)
  static getEditorData(id: number): Promise<CashPreAccEditorData> {
    return http
      .get<ApiResponse<CashPreAccEditorData>>(`cash-pre-accounting/${id}/editor-data`)
      .then(handleApiResponse);
  }

  // A kontír-szerkesztő mentése (K2D pb_ok) — a szerkesztő nyitva marad, csak újraolvas
  static save(id: number, request: CashPreAccSaveRequest): Promise<CashPreAccSaveResult> {
    return http
      .post<ApiResponse<CashPreAccSaveResult>>(`cash-pre-accounting/${id}/save`, request)
      .then(handleApiResponse);
  }

  //---------- Feladás-tételek (K2D pb_felad → w_popup_item_up_os) ----------//

  // A kivonat bizonylattételei a feladás-pipával
  static getUploadItems(id: number): Promise<CashPreAccUploadItem[]> {
    return http
      .get<ApiResponse<CashPreAccUploadItem[]>>(`cash-pre-accounting/${id}/upload-items`)
      .then(handleApiResponse);
  }

  // A feladás-pipák mentése (csak a változott tételeket küldjük)
  static saveUploadFlags(
    id: number,
    items: { docItemId: number; isUp: boolean }[]
  ): Promise<CashPreAccUploadFlagsResult> {
    return http
      .post<ApiResponse<CashPreAccUploadFlagsResult>>(`cash-pre-accounting/${id}/upload-flags`, {
        items,
      })
      .then(handleApiResponse);
  }

  //---------- A dimenzió-bontás füleinek kísérő-lookupjai ----------//

  static getEditorCars(): Promise<CashPreAccCar[]> {
    return http
      .get<ApiResponse<CashPreAccCar[]>>(`cash-pre-accounting/editor-lookups/cars`)
      .then(handleApiResponse);
  }

  static getEditorProjects(): Promise<CashPreAccProject[]> {
    return http
      .get<ApiResponse<CashPreAccProject[]>>(`cash-pre-accounting/editor-lookups/projects`)
      .then(handleApiResponse);
  }

  static getEditorPos(): Promise<CashPreAccPo[]> {
    return http
      .get<ApiResponse<CashPreAccPo[]>>(`cash-pre-accounting/editor-lookups/pos`)
      .then(handleApiResponse);
  }

  static getEditorUsers(): Promise<{ id: number; name: string }[]> {
    return http
      .get<ApiResponse<{ id: number; name: string }[]>>(`cash-pre-accounting/editor-lookups/users`)
      .then(handleApiResponse);
  }

  static getEditorStations(): Promise<{ id: number; name: string }[]> {
    return http
      .get<ApiResponse<{ id: number; name: string }[]>>(`cash-pre-accounting/editor-lookups/stations`)
      .then(handleApiResponse);
  }

  static getEditorTools(): Promise<CashPreAccTool[]> {
    return http
      .get<ApiResponse<CashPreAccTool[]>>(`cash-pre-accounting/editor-lookups/tools`)
      .then(handleApiResponse);
  }

  static validatePart(code: string): Promise<CashPreAccValidate> {
    return http
      .get<ApiResponse<CashPreAccValidate>>(
        `cash-pre-accounting/validate/part/${encodeURIComponent(code)}`)
      .then(handleApiResponse);
  }

  static validatePartner(name: string): Promise<CashPreAccValidate> {
    return http
      .get<ApiResponse<CashPreAccValidate>>(
        `cash-pre-accounting/validate/partner/${encodeURIComponent(name)}`)
      .then(handleApiResponse);
  }
}
